/* Altissimo — global scripts. No dependencies, loaded with `defer`. */
(() => {
  'use strict';

  const theme = window.theme || { routes: {}, strings: {} };
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- Utilities ---------- */
  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };
  const parseHTML = (text) => new DOMParser().parseFromString(text, 'text/html');
  const sectionInner = (html, selector) => {
    const doc = parseHTML(html);
    return doc.querySelector(selector);
  };
  const focusable = (root) =>
    [...root.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])')]
      .filter((el) => el.offsetParent !== null || el === document.activeElement);

  const scrollLock = {
    count: 0,
    lock() { if (this.count++ === 0) document.body.classList.add('is-locked'); },
    unlock() { if (this.count > 0 && --this.count === 0) document.body.classList.remove('is-locked'); }
  };

  const toast = (message) => {
    const el = document.getElementById('Toast');
    if (!el || !message) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(el._timer);
    el._timer = setTimeout(() => { el.hidden = true; }, 3200);
  };

  theme.utils = { debounce, parseHTML, sectionInner, toast, scrollLock };

  /* ---------- Sticky header ---------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.section-header') || this.parentElement;
      this.mode = this.dataset.sticky || 'on-scroll-up';
      this.lastY = window.scrollY;
      this.section.classList.toggle('is-sticky', this.mode !== 'none');
      // Non-sticky marker: gives the header's natural position even while it is stuck.
      if (!this.section.previousElementSibling || !this.section.previousElementSibling.matches('.header-sentinel')) {
        const sentinel = document.createElement('div');
        sentinel.className = 'header-sentinel';
        sentinel.setAttribute('aria-hidden', 'true');
        this.section.before(sentinel);
      }
      this.sentinel = this.section.previousElementSibling;

      this.setHeights = () => {
        const h = this.offsetHeight;
        document.documentElement.style.setProperty('--header-height', `${h}px`);
        this.updateOffset();
      };
      this.ro = new ResizeObserver(this.setHeights);
      this.ro.observe(this);

      this.onScroll = () => {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          this.update();
          this.ticking = false;
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.update();
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      this.ro && this.ro.disconnect();
    }
    update() {
      const y = Math.max(window.scrollY, 0);
      const top = this.sentinel ? this.sentinel.getBoundingClientRect().top + y : 0;
      this.section.classList.toggle('is-scrolled', this.mode !== 'none' && y > top + 4);
      if (this.mode === 'on-scroll-up' && !document.body.classList.contains('is-locked')) {
        const goingDown = y > this.lastY;
        const past = y > top + this.offsetHeight * 2;
        if (goingDown && past && Math.abs(y - this.lastY) > 4) this.section.classList.add('is-hidden');
        else if (!goingDown && Math.abs(y - this.lastY) > 4) this.section.classList.remove('is-hidden');
        if (y <= top) this.section.classList.remove('is-hidden');
      }
      this.lastY = y;
      this.updateOffset();
    }
    updateOffset() {
      const visible = this.mode !== 'none' && !this.section.classList.contains('is-hidden');
      document.documentElement.style.setProperty('--header-offset', visible ? `${this.offsetHeight}px` : '0px');
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* ---------- Desktop dropdown navigation ---------- */
  class HeaderNav extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('details')];
      const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
      // Start loading the menu photos as soon as the pointer comes near the menu.
      const warm = () => this.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
      this.addEventListener('pointerenter', warm, { once: true });
      this.addEventListener('focusin', warm, { once: true });
      this.items.forEach((details) => {
        details.addEventListener('toggle', () => {
          if (details.open) this.items.forEach((d) => { if (d !== details) d.open = false; });
        });
        details.addEventListener('mouseenter', () => {
          if (!fine.matches) return;
          clearTimeout(details._t);
          details._t = setTimeout(() => { details.open = true; }, 80);
        });
        details.addEventListener('mouseleave', () => {
          if (!fine.matches) return;
          clearTimeout(details._t);
          details._t = setTimeout(() => { details.open = false; }, 180);
        });
      });
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) this.closeAll();
      });
      this.addEventListener('keyup', (e) => {
        if (e.key === 'Escape') {
          const open = this.items.find((d) => d.open);
          if (open) { open.open = false; open.querySelector('summary').focus(); }
        }
      });
    }
    closeAll() { this.items.forEach((d) => { d.open = false; }); }
  }
  customElements.define('header-nav', HeaderNav);

  /* ---------- Drawers (menu, cart, filters) ---------- */
  class SideDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]')) this.close();
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        if (e.key === 'Tab') this.trap(e);
      });
    }
    open(trigger) {
      if (this.classList.contains('is-open')) return;
      this.trigger = trigger || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      if (this.trigger && this.trigger.hasAttribute('aria-expanded')) this.trigger.setAttribute('aria-expanded', 'true');
      scrollLock.lock();
      const target = this.querySelector('[data-autofocus]') || focusable(this.querySelector('.drawer__panel') || this)[0];
      setTimeout(() => target && target.focus({ preventScroll: true }), 60);
      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      if (this.trigger && this.trigger.hasAttribute('aria-expanded')) this.trigger.setAttribute('aria-expanded', 'false');
      scrollLock.unlock();
      if (this.trigger && document.contains(this.trigger)) this.trigger.focus({ preventScroll: true });
      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }
    trap(e) {
      const els = focusable(this.querySelector('.drawer__panel') || this);
      if (!els.length) return;
      const first = els[0];
      const last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }
  customElements.define('side-drawer', SideDrawer);

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (!opener) return;
    const drawer = document.getElementById(opener.dataset.drawerOpen);
    if (!drawer || typeof drawer.open !== 'function') return;
    e.preventDefault();
    drawer.open(opener);
  });

  /* ---------- Announcement bar ---------- */
  class AnnouncementBar extends HTMLElement {
    // One message at a time on every screen, fading from one to the next; pauses on hover and focus.
    connectedCallback() {
      this.items = [...this.querySelectorAll('.announcement__item')];
      if (this.items.length < 2) return;
      this.index = 0;
      this.delay = Number(this.dataset.speed || 5) * 1000;
      this.paused = false;
      const pause = () => { this.paused = true; };
      const resume = () => { this.paused = false; };
      this.addEventListener('mouseenter', pause);
      this.addEventListener('mouseleave', resume);
      this.addEventListener('focusin', pause);
      this.addEventListener('focusout', resume);
      this.timer = setInterval(() => { if (!this.paused && !document.hidden) this.next(); }, this.delay);
    }
    disconnectedCallback() {
      clearInterval(this.timer);
    }
    next() {
      this.items[this.index].classList.remove('is-active');
      this.items[this.index].setAttribute('aria-hidden', 'true');
      this.index = (this.index + 1) % this.items.length;
      this.items[this.index].classList.add('is-active');
      this.items[this.index].removeAttribute('aria-hidden');
    }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  /* ---------- Carousel rail: swipe on phones, arrows on computers scroll by what is visible ---------- */
  class CarouselRail extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('[data-rail]');
      if (!this.rail) return;
      this.prev = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');
      const step = (dir) => {
        const item = this.rail.firstElementChild;
        const gap = parseFloat(getComputedStyle(this.rail).columnGap) || 0;
        const width = item ? item.getBoundingClientRect().width + gap : this.rail.clientWidth;
        const perView = Math.max(1, Math.floor((this.rail.clientWidth + gap) / width));
        this.rail.scrollBy({ left: dir * width * perView, behavior: 'smooth' });
      };
      if (this.prev) this.prev.addEventListener('click', () => step(-1));
      if (this.next) this.next.addEventListener('click', () => step(1));
      this.update = () => {
        const max = this.rail.scrollWidth - this.rail.clientWidth - 2;
        if (this.prev) this.prev.disabled = this.rail.scrollLeft <= 2;
        if (this.next) this.next.disabled = this.rail.scrollLeft >= max;
        this.classList.toggle('is-static', max <= 0);
      };
      this.rail.addEventListener('scroll', () => requestAnimationFrame(this.update), { passive: true });
      window.addEventListener('resize', this.update);
      this.update();
    }
    disconnectedCallback() {
      if (this.update) window.removeEventListener('resize', this.update);
    }
  }
  customElements.define('carousel-rail', CarouselRail);

  /* ---------- Search modal + predictive search ---------- */
  class SearchModal extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      this.defaults = this.querySelector('[data-predictive-default]');
      this.enabled = this.dataset.predictive === 'true' && theme.routes.predictiveSearch;
      this.addEventListener('click', (e) => { if (e.target.closest('[data-search-close]')) this.close(); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
      if (this.enabled && this.input) {
        this.input.addEventListener('input', debounce(() => this.search(this.input.value.trim()), 200));
      }
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-search-open]');
        if (btn) { e.preventDefault(); this.open(btn); }
      });
    }
    open(trigger) {
      this.trigger = trigger;
      this.hidden = false;
      requestAnimationFrame(() => this.classList.add('is-open'));
      scrollLock.lock();
      setTimeout(() => this.input && this.input.focus(), 50);
    }
    close() {
      if (this.hidden) return;
      this.classList.remove('is-open');
      scrollLock.unlock();
      setTimeout(() => { this.hidden = true; }, 200);
      if (this.trigger) this.trigger.focus({ preventScroll: true });
    }
    async search(q) {
      if (this.controller) this.controller.abort();
      if (!q) {
        this.results.innerHTML = '';
        if (this.defaults) this.defaults.hidden = false;
        return;
      }
      this.controller = new AbortController();
      const params = new URLSearchParams({
        q,
        section_id: 'predictive-search',
        'resources[type]': 'product,collection,query,article,page',
        'resources[limit]': '6',
        'resources[options][fields]': 'title,product_type,variants.title,vendor,tag'
      });
      try {
        const res = await fetch(`${theme.routes.predictiveSearch}?${params}`, { signal: this.controller.signal });
        if (!res.ok) throw new Error(res.status);
        const content = sectionInner(await res.text(), '[data-predictive-content]');
        if (this.defaults) this.defaults.hidden = true;
        this.results.innerHTML = content ? content.innerHTML : '';
      } catch (err) {
        if (err.name !== 'AbortError') this.results.innerHTML = '';
      }
    }
  }
  customElements.define('search-modal', SearchModal);

  /* ---------- Cart ---------- */
  const cartSections = () => {
    const ids = ['cart-drawer', 'cart-icon-bubble'];
    const page = document.querySelector('[data-cart-page]');
    if (page) ids.push(page.dataset.sectionId);
    return ids;
  };

  const renderCart = (sections) => {
    if (!sections) return;
    if (sections['cart-icon-bubble']) {
      const bubble = sectionInner(sections['cart-icon-bubble'], '[data-cart-bubble]');
      document.querySelectorAll('[data-cart-bubble]').forEach((el) => { el.innerHTML = bubble ? bubble.innerHTML : ''; });
    }
    const drawer = document.getElementById('CartDrawer');
    if (drawer && sections['cart-drawer']) drawer.renderPanel(sections['cart-drawer']);
    const page = document.querySelector('[data-cart-page]');
    if (page && sections[page.dataset.sectionId]) {
      const fresh = sectionInner(sections[page.dataset.sectionId], '[data-cart-page]');
      if (fresh) page.innerHTML = fresh.innerHTML;
    }
  };

  const cartChange = async (line, quantity) => {
    const res = await fetch(`${theme.routes.cartChange}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity, sections: cartSections(), sections_url: window.location.pathname })
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.cartError);
    return data;
  };

  // Free gift lines (properties._gift_for = the saxophone's variant id) follow their saxophone's quantity,
  // and a gift left on its own is removed. Returns the update's response, or null when nothing had to change.
  const syncGifts = async (cart) => {
    if (!cart || !Array.isArray(cart.items)) return null;
    const giftFor = (item) => item.properties && item.properties._gift_for;
    const parents = {};
    cart.items.forEach((item) => {
      if (!giftFor(item)) parents[item.variant_id] = (parents[item.variant_id] || 0) + item.quantity;
    });
    const updates = {};
    cart.items.forEach((item) => {
      const parent = giftFor(item);
      if (parent) {
        const want = parents[parent] || 0;
        if (item.quantity !== want) updates[item.key] = want;
      } else if (item.product_type === 'Free gift') {
        updates[item.key] = 0;
      }
    });
    if (!Object.keys(updates).length) return null;
    const res = await fetch(`${theme.routes.cartUpdate}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates, sections: cartSections(), sections_url: window.location.pathname })
    });
    const data = await res.json();
    return res.ok && !data.status ? data : null;
  };

  class CartDrawer extends SideDrawer {
    renderPanel(html) {
      const fresh = sectionInner(html, '#CartDrawerPanel');
      const panel = this.querySelector('#CartDrawerPanel');
      if (!fresh || !panel) return;
      const body = panel.querySelector('.drawer__body');
      const scroll = body ? body.scrollTop : 0;
      const active = document.activeElement;
      const activeKey = active && active.closest('[data-line-key]');
      // Give focus back to the same control (the + or - button, or the number), so a click on + never leaves a ring on the number
      const activeControl = active && active.matches('[data-qty]') ? `[data-qty="${active.dataset.qty}"]` : '.quantity__input';
      panel.innerHTML = fresh.innerHTML;
      const newBody = panel.querySelector('.drawer__body');
      if (newBody) newBody.scrollTop = scroll;
      if (activeKey) {
        const line = panel.querySelector(`[data-line-key="${activeKey.dataset.lineKey}"]`);
        const again = line && (line.querySelector(activeControl) || line.querySelector('.quantity__input'));
        if (again) again.focus({ preventScroll: true });
      }
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  // Quantity changes + remove inside any cart lines container (drawer or page)
  const lineUpdate = (lineEl, qty) => {
    if (!lineEl) return;
    clearTimeout(lineEl._timer);
    lineEl._timer = setTimeout(async () => {
      lineEl.classList.add('is-updating');
      try {
        const data = await cartChange(Number(lineEl.dataset.line), qty);
        const synced = await syncGifts(data);
        renderCart((synced || data).sections);
      } catch (err) {
        lineEl.classList.remove('is-updating');
        toast(err.message);
      }
    }, qty === 0 ? 0 : 350);
  };

  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-cart-lines] .quantity__input');
    if (!input) return;
    const lineEl = input.closest('[data-line]');
    lineUpdate(lineEl, Math.max(0, parseInt(input.value, 10) || 0));
  });
  document.addEventListener('click', (e) => {
    const remove = e.target.closest('[data-cart-remove]');
    if (!remove) return;
    e.preventDefault();
    const lineEl = remove.closest('[data-line]');
    lineUpdate(lineEl, 0);
  });

  /* ---------- Quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-qty]');
        if (!btn || !this.input) return;
        e.preventDefault();
        const step = Number(this.input.step) || 1;
        const min = this.input.min !== '' ? Number(this.input.min) : 0;
        const max = this.input.max !== '' ? Number(this.input.max) : Infinity;
        const current = Number(this.input.value) || 0;
        const next = btn.dataset.qty === 'plus' ? current + step : current - step;
        const clamped = Math.min(Math.max(next, min), max);
        if (clamped === current) return;
        this.input.value = clamped;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
        this.sync();
      });
      this.input && this.input.addEventListener('change', () => this.sync());
      this.sync();
    }
    sync() {
      if (!this.input) return;
      const min = this.input.min !== '' ? Number(this.input.min) : 0;
      const minus = this.querySelector('[data-qty="minus"]');
      if (minus && !this.closest('[data-cart-lines]')) minus.disabled = Number(this.input.value) <= min;
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- Product form (ajax add to basket) ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    async onSubmit(e) {
      e.preventDefault();
      const submitter = e.submitter && e.submitter.type === 'submit' ? e.submitter : null;
      const buyNow = Boolean(submitter && submitter.hasAttribute('data-buy-now'));
      const button = submitter || this.form.querySelector('[type="submit"]');
      if (!button || button.disabled || button.classList.contains('is-loading')) return;
      const error = this.querySelector('[data-form-error]');
      if (error) { error.hidden = true; error.textContent = ''; }
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');

      const body = new FormData(this.form);
      body.append('sections', cartSections().join(','));
      body.append('sections_url', window.location.pathname);

      // A saxophone with free accessories: the gifts go in with it, same quantity, tagged with its variant id
      const gifts = (this.dataset.gifts || '').split(',').filter(Boolean);
      let request = { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body };
      if (gifts.length) {
        const id = Number(body.get('id'));
        const quantity = Math.max(1, parseInt(body.get('quantity'), 10) || 1);
        const properties = {};
        // The kit's own lines go in below, so the saxophone line does not also carry the kit as a note
        const kit = this.form.querySelector('[data-kit-property]');
        for (const [name, value] of body.entries()) {
          const match = name.match(/^properties\[(.+)\]$/);
          if (match && !(kit && name === kit.name)) properties[match[1]] = value;
        }
        const items = [{ id, quantity, properties }].concat(
          gifts.map((gift) => ({ id: Number(gift), quantity, properties: { _gift_for: String(id) } }))
        );
        request = {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: cartSections(), sections_url: window.location.pathname })
        };
      }

      try {
        const res = await fetch(`${theme.routes.cartAdd}.js`, request);
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.cartError);
        if (buyNow) {
          window.location.assign(`${theme.routes.root.replace(/\/$/, '')}/checkout`);
          return;
        }
        renderCart(data.sections);
        const drawer = document.getElementById('CartDrawer');
        if (theme.cartType === 'drawer' && drawer) drawer.open(button);
        else toast(theme.strings.added);
        document.dispatchEvent(new CustomEvent('cart:added', { detail: data }));
      } catch (err) {
        if (error) { error.textContent = err.message; error.hidden = false; }
        else toast(err.message);
      } finally {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------- Free accessories above Add to basket: one card at a time, next one every few seconds ---------- */
  class ProductGifts extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.pgifts__track');
      this.cards = this.track ? [...this.track.children] : [];
      this.index = 0;
      if (this.cards.length < 2) return;
      this.delay = Number(this.dataset.interval) || 2000;
      this.addEventListener('click', (e) => {
        const arrow = e.target.closest('[data-gift-step]');
        if (!arrow) return;
        this.go(this.index + Number(arrow.dataset.giftStep));
        this.restart();
      });
      ['mouseenter', 'focusin', 'touchstart'].forEach((ev) => this.addEventListener(ev, () => this.stop(), { passive: true }));
      ['mouseleave', 'focusout'].forEach((ev) => this.addEventListener(ev, () => this.restart()));
      this.addEventListener('touchend', () => this.restart(), { passive: true });
      document.addEventListener('visibilitychange', () => (document.hidden ? this.stop() : this.restart()));
      this.restart();
    }
    go(i) {
      const n = this.cards.length;
      this.index = ((i % n) + n) % n;
      this.track.style.transform = `translateX(${-100 * this.index}%)`;
      this.cards.forEach((card, k) => card.setAttribute('aria-hidden', k === this.index ? 'false' : 'true'));
    }
    stop() { clearInterval(this.timer); this.timer = null; }
    restart() {
      this.stop();
      if (reduceMotion.matches || document.hidden) return;
      this.timer = setInterval(() => this.go(this.index + 1), this.delay);
    }
    disconnectedCallback() { this.stop(); }
  }
  customElements.define('product-gifts', ProductGifts);

  /* ---------- Load more (collection / search) ---------- */
  class LoadMore extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;
      this.button.addEventListener('click', () => this.load());
    }
    async load() {
      const url = this.dataset.nextUrl;
      const grid = document.getElementById(this.dataset.grid);
      if (!url || !grid) return;
      this.button.classList.add('is-loading');
      try {
        const sep = url.includes('?') ? '&' : '?';
        const res = await fetch(`${url}${sep}section_id=${encodeURIComponent(this.dataset.sectionId)}`);
        const doc = parseHTML(await res.text());
        const newGrid = doc.getElementById(this.dataset.grid);
        if (newGrid) {
          const first = newGrid.firstElementChild;
          grid.append(...newGrid.children);
          const link = first && first.querySelector('a');
          if (link) link.focus({ preventScroll: true });
        }
        const next = doc.querySelector('load-more');
        if (next && next.dataset.nextUrl) {
          this.dataset.nextUrl = next.dataset.nextUrl;
          const progress = next.querySelector('.load-more__progress');
          const mine = this.querySelector('.load-more__progress');
          if (progress && mine) mine.textContent = progress.textContent;
        } else {
          this.remove();
        }
      } catch (err) {
        window.location.href = url;
      } finally {
        if (this.button) this.button.classList.remove('is-loading');
      }
    }
  }
  customElements.define('load-more', LoadMore);

  /* ---------- Sticky add to basket bar ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.target = document.getElementById(this.dataset.target);
      this.form = document.getElementById(this.dataset.form);
      if (!this.target || !('IntersectionObserver' in window)) return;
      this.passed = false;
      this.inFooter = false;
      // Shown once the main button has scrolled away, hidden again while the footer is on screen
      this.io = new IntersectionObserver(([entry]) => {
        this.passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.update();
      });
      this.io.observe(this.target);
      this.footer = document.querySelector('.footer');
      if (this.footer) {
        this.footerIo = new IntersectionObserver(([entry]) => {
          this.inFooter = entry.isIntersecting;
          this.update();
        });
        this.footerIo.observe(this.footer);
      }
      this.addEventListener('click', (e) => {
        if (!e.target.closest('[data-sticky-submit]')) return;
        e.preventDefault();
        if (this.target.disabled) return;
        if (this.form && this.form.requestSubmit) this.form.requestSubmit(this.target);
        else this.target.click();
      });
    }
    update() {
      const show = this.passed && !this.inFooter;
      this.classList.toggle('is-visible', show);
      this.setAttribute('aria-hidden', show ? 'false' : 'true');
      this.querySelectorAll('button, a').forEach((el) => { el.tabIndex = show ? 0 : -1; });
    }
    disconnectedCallback() {
      this.io && this.io.disconnect();
      this.footerIo && this.footerIo.disconnect();
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* ---------- Product film: a play button on the poster, the browser's controls once it plays ---------- */
  class ProductFilm extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.button = this.querySelector('[data-film-play]');
      if (!this.video || !this.button) return;
      this.video.controls = false;
      this.button.hidden = false;
      this.button.addEventListener('click', () => {
        this.button.hidden = true;
        this.video.controls = true;
        const playing = this.video.play();
        if (playing && playing.catch) playing.catch(() => {});
      });
    }
  }
  customElements.define('product-film', ProductFilm);

  /* ---------- Silent looping videos: load when close to the screen, play while on screen, pause off screen.
     If the phone refused to start it (power saving), the first touch on the page starts it. ---------- */
  const autoVideos = (root = document) => {
    root.querySelectorAll('video[data-auto-video]').forEach((video) => {
      if (video._auto) return;
      video._auto = true;
      video.muted = true;
      let onScreen = false;
      const load = () => {
        if (video._loaded) return;
        video._loaded = true;
        video.querySelectorAll('source[data-src]').forEach((s) => { s.src = s.dataset.src; s.removeAttribute('data-src'); });
        video.load();
      };
      const play = () => {
        if (!onScreen) return;
        load();
        const playing = video.play();
        if (playing && playing.catch) playing.catch(() => {});
      };
      const kick = () => play();
      const events = ['touchend', 'click', 'keydown'];
      events.forEach((ev) => document.addEventListener(ev, kick, { passive: true }));
      video.addEventListener('playing', () => events.forEach((ev) => document.removeEventListener(ev, kick)), { once: true });
      video.addEventListener('canplay', play);
      if (!('IntersectionObserver' in window)) { onScreen = true; play(); return; }
      const near = new IntersectionObserver((entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        near.disconnect();
        load();
      }, { rootMargin: '800px 0px' });
      near.observe(video);
      new IntersectionObserver((entries) => {
        onScreen = entries.some((e) => e.isIntersecting);
        if (onScreen) play();
        else if (!video.paused) video.pause();
      }).observe(video);
    });
  };
  autoVideos();
  document.addEventListener('shopify:section:load', (e) => autoVideos(e.target));

  /* ---------- Saxophone sound: brass play button, progress bar to seek, shown once the file loads ---------- */
  class ProductSound extends HTMLElement {
    connectedCallback() {
      this.audio = this.querySelector('audio');
      this.toggle = this.querySelector('[data-sound-toggle]');
      this.bar = this.querySelector('[data-sound-bar]');
      this.fill = this.querySelector('[data-sound-fill]');
      this.time = this.querySelector('[data-sound-time]');
      if (!this.audio || !this.toggle) return;
      // Shown straight away (iPhones may not load the metadata before a tap); hidden again if the file is missing.
      const hint = this.nextElementSibling && this.nextElementSibling.matches('[data-sound-hint]') ? this.nextElementSibling : null;
      const missing = () => {
        this.hidden = true;
        if (hint) hint.hidden = false;
      };
      if (this.audio.error) missing();
      this.audio.addEventListener('error', missing);
      this.audio.addEventListener('loadedmetadata', () => this.render());
      if (this.audio.readyState >= 1) this.render();
      // Start buffering as soon as the visitor gets close to the button, so the first note plays on the click.
      const warm = () => { if (this.audio.preload !== 'auto') this.audio.preload = 'auto'; };
      ['pointerenter', 'touchstart', 'focusin'].forEach((ev) => this.addEventListener(ev, warm, { once: true, passive: true }));

      this.toggle.addEventListener('click', () => {
        if (this.audio.paused) {
          // Silent looping videos keep playing: only other sounds and films are stopped
          document.querySelectorAll('audio, video').forEach((m) => { if (m !== this.audio && !(m.muted && m.loop)) m.pause(); });
          const playing = this.audio.play();
          if (playing && playing.catch) playing.catch(() => {});
        } else this.audio.pause();
      });
      this.audio.addEventListener('play', () => this.setPlaying(true));
      this.audio.addEventListener('pause', () => this.setPlaying(false));
      this.audio.addEventListener('ended', () => { this.audio.currentTime = 0; this.render(); });
      this.audio.addEventListener('timeupdate', () => this.render());

      if (this.bar) {
        const seek = (e) => {
          const r = this.bar.getBoundingClientRect();
          const x = Math.min(Math.max((e.clientX - r.left) / r.width, 0), 1);
          if (this.audio.duration) { this.audio.currentTime = x * this.audio.duration; this.render(); }
        };
        this.bar.addEventListener('pointerdown', (e) => {
          seek(e);
          this.bar.setPointerCapture(e.pointerId);
          const move = (ev) => seek(ev);
          const up = () => { this.bar.removeEventListener('pointermove', move); this.bar.removeEventListener('pointerup', up); };
          this.bar.addEventListener('pointermove', move);
          this.bar.addEventListener('pointerup', up);
        });
        this.bar.addEventListener('keydown', (e) => {
          const step = { ArrowRight: 5, ArrowUp: 5, ArrowLeft: -5, ArrowDown: -5 }[e.key];
          if (!step || !this.audio.duration) return;
          e.preventDefault();
          this.audio.currentTime = Math.min(Math.max(this.audio.currentTime + step, 0), this.audio.duration);
          this.render();
        });
      }
    }
    setPlaying(on) {
      this.classList.toggle('is-playing', on);
      const label = this.toggle.getAttribute('aria-label').replace(/^(Play|Pause): /, '');
      this.toggle.setAttribute('aria-label', (on ? 'Pause: ' : 'Play: ') + label);
    }
    render() {
      const d = this.audio.duration || 0;
      const t = this.audio.currentTime || 0;
      const pct = d ? (t / d) * 100 : 0;
      if (this.fill) this.fill.style.width = pct + '%';
      if (this.bar) this.bar.setAttribute('aria-valuenow', Math.round(pct));
      const fmt = (s) => Math.floor(s / 60) + ':' + String(Math.floor(s % 60)).padStart(2, '0');
      if (this.time) this.time.textContent = d ? fmt(t > 0 ? t : d) : '';
    }
  }
  customElements.define('product-sound', ProductSound);

  /* ---------- Product recommendations (loaded when close to the viewport) ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.children.length || !this.dataset.url) return;
      const load = async () => {
        try {
          const res = await fetch(this.dataset.url);
          const fresh = sectionInner(await res.text(), 'product-recommendations');
          if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
          else this.closest('.section') && (this.closest('.section').hidden = true);
        } catch (err) { /* ignore */ }
      };
      if (!('IntersectionObserver' in window)) { load(); return; }
      const io = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        load();
      }, { rootMargin: '0px 0px 1400px 0px' });
      io.observe(this);
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- Cart note ---------- */
  document.addEventListener('change', debounce((e) => {
    const note = e.target.closest && e.target.closest('[data-cart-note]');
    if (!note) return;
    fetch(`${theme.routes.cartUpdate}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note: note.value })
    });
  }, 300));

  /* ---------- Theme editor support ---------- */
  document.addEventListener('shopify:section:load', () => scrollLock.count && (scrollLock.count = 0, document.body.classList.remove('is-locked')));
  document.addEventListener('shopify:block:select', (e) => {
    const details = e.target.closest('details') || (e.target.tagName === 'DETAILS' ? e.target : null);
    if (details) details.open = true;
  });

  window.theme = theme;
})();
